import { inject, Injectable } from '@angular/core';
import { SyncQueueService } from 'src/app/core/services/sync/sync-queue.service';
import { DocumentTreeNode } from 'src/app/features/bizzmine/widgets/document-tree-widget/document-tree-widget.component';
import { catchError, filter, firstValueFrom, forkJoin, Observable, of, shareReplay, take, tap } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { KeepOfflineService } from 'src/app/core/services/keep-offline/keep-offline.service';
import { CollectionListApiService } from 'src/app/api/bizzmine/collection-list/collection-list-api.service';
import { OfflineSyncType } from 'src/app/shared/components/grid/cells/grid-cell-extension/grid-cell-extension.component';
import { DocumentTreeApiService } from 'src/app/api/bizzmine/document-tree/document-tree-api.service';
import { DocumentTreeWidgetData } from 'src/models/ts/document-tree-widget-data.model';
import { GridDataDto } from 'src/models/ts/grid-data-dto.model';
import { PwaSyncType } from 'src/models/ts/pwa-sync-type.model';
import { SyncItemType } from 'src/models/ts/sync-item';
import { Store } from '@ngrx/store';
import { workspaceSidebarFeature } from 'src/app/store/features/workspace-sidebar/workspace-sidebar-feature';
import { PwaSyncModel } from 'src/models/ts/pwa-sync.model';
import { PwaApiService } from 'src/app/api/bizzmine/pwa/pwa-api.service';
import { PwaSyncInstance } from 'src/models/ts/pwa-sync-instance.model';
import { PwaSyncFolder } from 'src/models/ts/pwa-sync-folder.model';
import { CollectionForm } from 'src/models/ts/collection-form.model';
import { PwaSyncUpdateType } from 'src/models/ts/pwa-sync-update-type.model';
import { PwaSyncModificationType } from 'src/models/ts/modification-type.model';
import { DocumentTreeFolderDto } from 'src/models/ts/document-tree-folder-dto.model';

@Injectable({
  providedIn: 'root'
})
export class SyncService {

  private syncQueueService = inject(SyncQueueService);
  private keepOfflineService = inject(KeepOfflineService);
  private collectionListApiService = inject(CollectionListApiService);
  private documentTreeApiService = inject(DocumentTreeApiService);
  private store$ = inject(Store);
  private pwaApiService = inject(PwaApiService);

  public syncInstance(
    collectionId: number,
    instanceId: number,
    versionId: number,
    widgetId: number | undefined,
    observable: Observable<unknown>,
    listId?: number,
  ): void {
    this.syncQueueService.play();
    this.keepOfflineService.setDownloadingInstance(collectionId, instanceId, versionId, widgetId!, listId!);
    const itemId = widgetId+ '-' + listId+'-'+ collectionId;
    const collectionName = this.store$.selectSignal(workspaceSidebarFeature.selectParents)().find(p => p.Parent.WorkspaceItemsID === widgetId)?.Parent.Name;
    this.syncQueueService.addToQueue({
      id: itemId,
      name: collectionName,
      type: SyncItemType.SINGLE,
      observables: [observable],
      currentProgress: 0,
      totalProgress: 1,
    });
    this.syncQueueService.currentItemComplete$
      .pipe(filter(res => res.id === itemId), take(1), catchError((error) => {console.error('Error caught in pipe:', error); return of([])}))
      .subscribe((data) => {
        if(data.hasFailed){
          this.keepOfflineService.setFailedInstance(collectionId, instanceId);
        }else{
          const fd = data.result.formData;
          this.keepOfflineService.addOfflineInstance(collectionId, instanceId, fd.CollectionFormId, fd.InstanceLastModifiedOn, fd.FormLastModifiedOn);
        }
      });
  }

  public unlinkInstance(
    collectionId: number,
    instanceId: number,
    versionId: number,
    folderId: number,
  ): void {
    // Refetch list data of folder
    const folderData = this.keepOfflineService.offlineItems.folders.find(f => f.folderId === folderId);
    this.startSubscribingArrayObservable(folderData?.widgetId+'-', '', SyncItemType.METADATA, [this.keepOfflineService.saveFolderListSearchObservable(folderData?.listId!, folderId, true)], [folderId]);
    this.keepOfflineService.deleteInstanceCache(collectionId, instanceId, versionId, folderId, false);
  }

  public deleteInstance(
    collectionId: number,
    instanceId: number,
    versionId: number,
    folderId?: number,
  ): void {
    // Refetch list data of folders and views where the instance was stored
    this.startSubscribingArrayObservable('', '', SyncItemType.METADATA, this.keepOfflineService.createListSearchObservableFromAllInstances(collectionId, instanceId, versionId), []);
    this.keepOfflineService.deleteInstanceCache(collectionId, instanceId, versionId, folderId, true);
  }

  public async deleteDocumentTreeFolder(node: DocumentTreeNode, docTreeWidgetData: DocumentTreeWidgetData): Promise<void> {
    const allFolders = await this.fetchChildrenFolder(node, docTreeWidgetData, [node]);
    this.keepOfflineService.deleteOfflineFolder(allFolders.map(f=> f.ID));
  }

  public async unsyncDocumentTreeFolder(node: DocumentTreeNode, docTreeWidgetData: DocumentTreeWidgetData): Promise<void> {
    const allFolders = await this.fetchChildrenFolder(node, docTreeWidgetData, [node]);
    this.keepOfflineService.removeOfflineFolder(allFolders.map(f=> f.ID));
  }

  public async removeFolder(node: DocumentTreeNode, docTreeWidgetData: DocumentTreeWidgetData): Promise<void> {
    const allFolders = await this.fetchChildrenFolder(node, docTreeWidgetData, [node]);
    allFolders.forEach(folderNode => {
      this.keepOfflineService.deleteFolderStructureInIndexedDb(docTreeWidgetData.ID, folderNode)
    })
  }

  public async fetchFolderStructure(node: DocumentTreeNode, docTreeWidgetData: DocumentTreeWidgetData): Promise<void> {
    const allFolders = await this.fetchChildrenFolder(node, docTreeWidgetData, [node]);
    const observables: Observable<HttpResponse<DocumentTreeFolderDto[]>>[] = [];
    allFolders.forEach(folderNode => {
      observables.push(this.documentTreeApiService.getDocumentTreeDataReponse(docTreeWidgetData.ID, folderNode.ID, {
        CanViewHiddenDocuments: docTreeWidgetData.CanViewHiddenDocuments
      }).pipe(tap((res) => {
        if (res != null && res.url != null && res.body != null) {
          this.keepOfflineService.saveDataInIndexedDb(res.url, res.body);
        }
      })
    ));
  });
    forkJoin(observables).subscribe();
  }

  private documentTreeDataObservable(node: DocumentTreeNode, docTreeWidgetData: DocumentTreeWidgetData): Observable<DocumentTreeFolderDto[]> {
    return this.documentTreeApiService.getDocumentTreeData(docTreeWidgetData.ID, node.ID, {
      CanViewHiddenDocuments: docTreeWidgetData.CanViewHiddenDocuments,
    })
  }

  private async fetchChildrenFolder(node: DocumentTreeNode, docTreeWidgetData: DocumentTreeWidgetData, collectionNodes: DocumentTreeNode[]): Promise<DocumentTreeNode[]> {
		if (!collectionNodes.find(n => n.ID == node.ID)) {
			collectionNodes.push(node);
		}

		const results = await firstValueFrom(this.documentTreeDataObservable(node, docTreeWidgetData));

		const children: DocumentTreeNode[] | null = results as DocumentTreeNode[] | null;

		if (children && children.length > 0) {
			node.children = children;
			node.hasChildren = children.length > 0;
		}

		if (node.hasChildren && node.children && node.children.length > 0) {
			// Process each child and collect their observables
			for (const child of node.children) {
				await this.fetchChildrenFolder(child, docTreeWidgetData, collectionNodes);
			}
		}
		return collectionNodes;
	}

  /**
   *
   * @param {DocumentTreeNode} node
   * @param {DocumentTreeWidgetData} docTreeWidgetData
   * @returns {Promise<void>}
   */
  public async syncDocumentTreeFolder(
    node: DocumentTreeNode,
    docTreeWidgetData: DocumentTreeWidgetData
  ): Promise<void> {
    this.syncQueueService.play();
    if (!node || !docTreeWidgetData) {
      console.error('No valid node provided for syncDocumentTreeFolder');
      return;
    }
    // Check if the node is already being downloaded
    const itemId = this.generateQueueIdForNode(node, docTreeWidgetData);
    if (this.syncQueueService.isInQueue(itemId)) {
      console.warn(`Syncing folder ${node.text} is already in progress:`);
      return;
    }

    try {
      const collectionCacheIds = [node.ID];
      // Wait for all observables from the folder hierarchy
      const allRequests = await this.fetchDataFolder(node, docTreeWidgetData, collectionCacheIds);
      console.debug(` Folder: ${node.text} has a total of  ${allRequests.length} records (requests)`);


      // Now we have all observables, start the subscription process
      this.startSubscribingArrayObservable(itemId, node.text, SyncItemType.FOLDER, allRequests, collectionCacheIds);
    } catch (error) {
      console.error('Error collecting folder data requests:', error);
    }
  }

  public async syncDocumentTreeFolderStructure(rootNode: DocumentTreeNode, docTreeWidgetData: DocumentTreeWidgetData): Promise<void> {
    //get all data of folder #hardcoded
    const dataRequests = await this.getAllTreeFoldersSearchListDataRequests(rootNode.children, docTreeWidgetData);
    const collectionCacheIds = [rootNode.ID];
    const itemId = this.generateQueueIdForNode(rootNode, docTreeWidgetData);

    // Now we have all observables, start the subscription process
    this.startSubscribingArrayObservable(itemId, docTreeWidgetData.Caption, SyncItemType.METADATA, dataRequests, collectionCacheIds);
  }

  /**
   * Fetches the listoptions,list data and subfolders of a folder and its children recursively, then retrieves all the formData observables,
   * finally it starts the sync operation that subscribes all the observables and saves the data in IndexedDB.
   * @param node The folder node to fetch data for.
   * @param prevcollectionCacheIds Previous collection cache IDs to append to.
   * @param nestedLevel The current nested level of the folder (default is 0). (should not be set by the caller)
   */
  private async fetchDataFolder(
    node: DocumentTreeNode,
    docTreeWidgetData: DocumentTreeWidgetData,
    collectionCacheIds: Array<number>
  ): Promise<Observable<unknown>[]> {
    const dataRequests$: Observable<unknown>[] = [];
    const cacheKeyId = node.ID;

    if (!collectionCacheIds.includes(cacheKeyId)) {
      collectionCacheIds.push(cacheKeyId);
    }

    this.keepOfflineService.setDownloadingFolder(node.ID, docTreeWidgetData.ID, node.ListsID, node.CollectionsID);
    console.debug(` Downloading folder: ${node.text}`, node);

    try {
      if(!node.IsRootFolder){

      const results = await firstValueFrom(forkJoin([
        this.collectionListApiService.getListOptionsReponse(node.CollectionsID, node.CollectionViewsID, node.ListsID),
        this.documentTreeApiService.getFolderDataResponse(node.ListsID, node.ID, {
          ListID: node.ListsID,
          Records: 999999
        }),
        this.documentTreeApiService.getDocumentTreeDataReponse(docTreeWidgetData.ID, node.ID, {
          CanViewHiddenDocuments: docTreeWidgetData.CanViewHiddenDocuments
        })
      ]));

      // Save results in IndexedDB
      results.forEach((res) => {
        if(res != null && res.url != null && res.body != null){
          this.keepOfflineService.saveDataInIndexedDb(res.url, res.body);
        }
      });

      const gridData: GridDataDto | null = results[1].body;
      const children: DocumentTreeNode[] | null = results[2].body as DocumentTreeNode[] | null;

      if (children) {
        node.children = children;
        node.hasChildren = children.length > 0;
      }

      if (gridData) {
        const records = gridData.Data;
        console.debug(` Folder: ${node.text} has ${records.length} items`);

        // Process current folder's items
        records.forEach((element: any) => {
          this.syncQueueService.setInstanceState(element.CollectionsID, element.ID, OfflineSyncType.Downloading);
          const getFormByInstanceReadResponse = this.keepOfflineService
            .createFormByInstanceReadWithPreviewFileResponseObservable(
              element.CollectionsID,
              element.ID,
              element.VersionsID,
              element.GuidChecksum,
              node.ID,
              PwaSyncType.Offline,
              PwaSyncUpdateType.None
            )
            .pipe(
              catchError((error) => {
                return of(error);
              })
            );

          dataRequests$.push(getFormByInstanceReadResponse);
        });
      }
    }
        // Process children recursively
        if (node.hasChildren && node.children && node.children.length > 0) {
          console.debug(`${node.text} has ${node.children.length} children`);

          // Process each child and collect their observables
          for (const child of node.children) {
            console.debug(`${node.text} fetching child ${child.text}`);
            const childRequests = await this.fetchDataFolder(child, docTreeWidgetData, collectionCacheIds);
            // Add child requests to our collection
            dataRequests$.push(...childRequests);
          }
        }

      return dataRequests$;
    } catch (error) {
      console.error('Error fetching folder data:', error);
      return dataRequests$;
    }
  }

  private async getAllTreeFoldersSearchListDataRequests(
    nodes: DocumentTreeNode[],
    docTreeWidgetData: DocumentTreeWidgetData,
    prevRequests?: Observable<any>[],
    prevUniqueRequests?: Set<string>
  ): Promise<Observable<any>[]> {
    const dataRequests$: Observable<any>[] = prevRequests ?? [];
    const uniqueRequests = prevUniqueRequests ?? new Set<string>(); // Track unique requests

    for (const node of nodes) {
      const listOptionsRequest = this.collectionListApiService
        .getListOptionsReponse(node.CollectionsID, node.CollectionViewsID, node.ListsID)
        .pipe(
          tap((res) => {
            if(res != null && res.url != null && res.body != null){
              this.keepOfflineService.saveDataInIndexedDb(res.url, res.body);
            }
          })
        );
      const folderDataRequest = this.documentTreeApiService
        .getFolderDataResponse(node.ListsID, node.ID, { ListID: node.ListsID, Records: 999999 })
        .pipe(
          tap((data) => {
            if (data.url != null) {
              this.keepOfflineService.saveDataInIndexedDb(data.url, data.body);
            }
          })
        );

      //We need to fetch the document tree data for each node to get the children
      const documentTreeDataRequest = this.documentTreeApiService
        .getDocumentTreeDataReponse(docTreeWidgetData.ID, node.ID, { CanViewHiddenDocuments: docTreeWidgetData.CanViewHiddenDocuments });
      const docTreeData = await firstValueFrom(documentTreeDataRequest);
      if (docTreeData.body && docTreeData.url)
        this.keepOfflineService.saveDataInIndexedDb(docTreeData.url, docTreeData.body);

      if (!uniqueRequests.has(node.CollectionsID + '_' + node.CollectionViewsID + '_' + node.ListsID + '_listOptions')) {
        uniqueRequests.add(node.CollectionsID + '_' + node.CollectionViewsID + '_' + node.ListsID + '_listOptions');
        dataRequests$.push(listOptionsRequest);
      }

      if (!uniqueRequests.has(node.ListsID + '_' + node.ID + '_folderData')) {
        uniqueRequests.add(node.ListsID + '_' + node.ID + '_folderData');
        dataRequests$.push(folderDataRequest);
      }

      if (!uniqueRequests.has(docTreeWidgetData.ID + '_' + node.ID + '_documentTreeData')) {
        uniqueRequests.add(docTreeWidgetData.ID + '_' + node.ID + '_documentTreeData');
        dataRequests$.push(documentTreeDataRequest);
      }

      if (node.hasChildren && docTreeData.body) {
        const data = docTreeData.body as DocumentTreeNode[];
        await this.getAllTreeFoldersSearchListDataRequests(data, docTreeWidgetData, dataRequests$, uniqueRequests);
      }
    }
    return dataRequests$;
  }

  public startSubscribingArrayObservable(syncId: string, itemName: string, type: SyncItemType, requests: Observable<unknown>[], collectionCacheIds: Array<number>): void {
    if(requests.length === 0) {
      this.markFoldersAsOffline(collectionCacheIds, OfflineSyncType.Downloaded);
      return;
    }
    this.syncQueueService.addToQueue({
      id: syncId,
      name: itemName,
      subfolders: collectionCacheIds.length - 1,
      currentProgress: 0,
      totalProgress: requests.length,
      type: type,
      observables: requests
    });
    this.syncQueueService.currentItemComplete$
      .pipe(filter((res) => res.id === syncId))
      .subscribe({
        next: (value) => {
          if(value.type == SyncItemType.METADATA) return; 
          if (value.completed) {
            this.markFoldersAsOffline(collectionCacheIds, value.hasFailed ? OfflineSyncType.Failed :  OfflineSyncType.Downloaded);
          } else {
            if(value.hasFailed){
              this.keepOfflineService.addOfflineFolderInstance(value.data.instanceId, value.data.versionId, value.data.collectionFormId, value.data.instanceLastModifiedOn, value.data.formLastModifiedOn, value.data.folderId, OfflineSyncType.Failed);
            }else{
              let data;
              if (value.result instanceof HttpResponse) {
                data = value.result.body;
              } else {
                data = value.result.formData;
              }
              this.keepOfflineService.addOfflineFolderInstance(data.InstancesID, data.VersionsID, data.CollectionFormId, data.InstanceLastModifiedOn, data.FormLastModifiedOn, value.result?.folderId, OfflineSyncType.Downloaded);
            }
          }
        }
      });
  }

  private markFoldersAsOffline(collectionCacheIds: Array<number>, status: OfflineSyncType): void {
    collectionCacheIds.forEach((collectionCacheId) => {
      this.keepOfflineService.addOfflineFolder(collectionCacheId, status);
    });
  }

  private generateQueueIdForNode(
    node: DocumentTreeNode,
    docTreeWidgetData: DocumentTreeWidgetData
  ): string {
    return `${docTreeWidgetData.ID}-${node.CollectionsID}-${node.CollectionViewsID}-${node.ListsID}-${node.id}`;
  }


  
  /**
   * Synchronizes offline items with the server by sending the current state of instances, folders, and views.
   *
   * The method constructs a payload containing all offline items and the last known online timestamp,
   * then sends it to the `/api/keep-offline/sync` endpoint via an HTTP POST request.
   *
   * On successful synchronization, it removes the 'appLastOnline' entry from local storage.
   * Errors during the synchronization process are logged to the console.
   */
  public sync(): void {
    this.pwaApiService.sync(this.keepOfflineService.parsOfflineItemsToPwaPayload()).pipe(shareReplay(1)).subscribe({
      next: (res: PwaSyncModel) => {
        if (res) {
          this.handleSyncResponse(res);
        }
      },
      error: (error) => {
        console.error('Error syncing keep offline items', error);
      }
    });
  }

  public handleSyncResponse(res: PwaSyncModel): void {
    //Handle Folders
    this.handleSyncFolders(res.Folders);

    //Handle Instances + fetch list data of instance
    this.handleSyncInstances(res.Instances);

    // If all items are synced successfully, clear the appLastOnline entry
    //this.bizzmineLocalStorage.removeItem('appLastOnline');
  }

  private handleSyncFolders(folders: PwaSyncFolder[]): void {
    const arrayWidgets: Map<number, {
      widgetId: number;
      dataObservables: Observable<any>[];
      structureObservables: Observable<any>[];
      folders: number[];
    }> = new Map();

    folders.forEach((folder) => {
      if (folder.Modified) {
        const structureObservables: Observable<any>[] = [];

        //1. Fetch list data
        structureObservables.push(this.keepOfflineService.saveFolderListSearchObservable(folder.ListsId!, folder.FolderId, true));

        //2. Set loader for folder
        this.keepOfflineService.setDownloadingFolder(folder.FolderId, folder.WidgetId, folder.ListsId!, folder.CollectionId);

        //3. Fetch all instances
        const instanceObservables: Observable<any>[] = [];
        folder.Instances.forEach((instance) => {
           const handleState = this.handleItemChangeByState(instance, folder.FolderId);
          if (instance.ModificationType !== PwaSyncModificationType.Deleted) {
            instanceObservables.push(handleState!);
          }
        });
        
        //4. Add folder to arrayWidgets
        if(arrayWidgets.has(folder.WidgetId)){
          const arrayWidget = arrayWidgets.get(folder.WidgetId);
          if(arrayWidget){
          arrayWidgets.set(folder.WidgetId, {
            widgetId: folder.WidgetId,
            dataObservables: arrayWidget.dataObservables.concat(instanceObservables),
            structureObservables: arrayWidget.structureObservables.concat(structureObservables),
            folders: arrayWidget.folders.concat(folder.FolderId)
          });
          }
        }else{
          arrayWidgets.set(folder.WidgetId, {
            widgetId: folder.WidgetId,
            dataObservables: instanceObservables,
            structureObservables: structureObservables,
            folders: [folder.FolderId]
          });
        }
      }
    });
    
    //5. Sync array widgets
    arrayWidgets.forEach((value) => {
      const collectionName = this.store$.selectSignal(workspaceSidebarFeature.selectParents)().find(p => p.Parent.WorkspaceItemsID === value.widgetId)?.Parent.Name as string;
      this.startSubscribingArrayObservable(value.widgetId+'-'+ SyncItemType.METADATA, '', SyncItemType.METADATA, value.structureObservables, value.folders);
      this.startSubscribingArrayObservable(value.widgetId+'-'+ SyncItemType.FOLDER, collectionName, SyncItemType.FOLDER, value.dataObservables, value.folders);
    })
  }

  private handleSyncInstances(instances: PwaSyncInstance[]): void {
    let observables: Observable<any>[] = [];

    instances.forEach((instance) => {
      const handleState = this.handleItemChangeByState(instance);
      if (instance.ModificationType !== PwaSyncModificationType.Deleted) {
        this.syncInstance(instance.CollectionId!, instance.InstanceId, instance.VersionId!, instance.WidgetId!, handleState!, instance.ViewListId! );
      }
    });

    // get unique objects lists & widgetId of instances
    const uniqueLists = instances.filter((item, index, arr) =>
      index === arr.findIndex(instance => instance.ViewListId === item.ViewListId && instance.WidgetId === item.WidgetId)
    );

    // Fetch list data for each unique instance
    uniqueLists.forEach((instance) => {
      observables.push(this.keepOfflineService.saveListSearchObservable(instance.ViewListId!, instance.WidgetId!, true));
    });

    this.startSubscribingArrayObservable('instance'+ '-'+ SyncItemType.METADATA, '', SyncItemType.METADATA, observables, []);
  }


  public handleItemChangeByState(item: PwaSyncInstance, folderId?: number): Observable<any> | null {
    let observable: Observable<any> | null = null;
    if(!item.CollectionId || !item.VersionId ){
      console.error('Missing parameter for item', item);
      return observable; 
    }
    
    // check what state the item is in
    switch (item.ModificationType) {
      case PwaSyncModificationType.Created:
        // instance loader
        if (folderId) {
          this.syncQueueService.setInstanceState(item.CollectionId, item.InstanceId, OfflineSyncType.Downloading);
        } else {
          this.keepOfflineService.setDownloadingInstance(item.CollectionId, item.InstanceId, item.VersionId, item.WidgetId!, item.ViewListId!);
        }
        //Offline = 1
        observable = this.keepOfflineService.createFormByInstanceReadWithPreviewFileResponseObservable(
          item.CollectionId,
          item.InstanceId,
          item.VersionId,
          item.Checksum,
          folderId,
          PwaSyncType.Sync,
          item.UpdateTypes!
        ).pipe(
          tap((res) => {
            if (res != null) {
              const fd = res.formData as CollectionForm;
              if (folderId) {
                this.keepOfflineService.addOfflineFolderInstance(item.InstanceId, item.VersionId!, fd.CollectionFormId!, fd.InstanceLastModifiedOn!, fd.FormLastModifiedOn, folderId, OfflineSyncType.Downloaded);
              } else {
                this.keepOfflineService.addOfflineInstance(item.CollectionId!, item.InstanceId!, fd.CollectionFormId!, fd.InstanceLastModifiedOn!, fd.FormLastModifiedOn);
              }
            }
          })
        );
        break;
      case PwaSyncModificationType.MediaUpdated:
        // instance loader
        if (folderId) {
          this.syncQueueService.setInstanceState(item.CollectionId, item.InstanceId, OfflineSyncType.Downloading);
        } else {
          this.keepOfflineService.setDownloadingInstance(item.CollectionId, item.InstanceId, item.VersionId, item.WidgetId!, item.ViewListId!);
        }
        //Offline = 1
        observable = this.keepOfflineService.createFormByInstanceReadWithPreviewFileResponseObservable(
          item.CollectionId!,
          item.InstanceId,
          item.VersionId!,
          item.Checksum,
          folderId,
          PwaSyncType.Sync,
          item.UpdateTypes!
        ).pipe(
          tap((res) => {
            if (res != null) {
              const fd = res.formData as CollectionForm;
              if (folderId) {
                this.keepOfflineService.addOfflineFolderInstance(item.InstanceId, item.VersionId!, fd.CollectionFormId!, fd.InstanceLastModifiedOn!, fd.FormLastModifiedOn, folderId, OfflineSyncType.Downloaded);
              } else {
                this.keepOfflineService.addOfflineInstance(item.CollectionId!, item.InstanceId!, fd.CollectionFormId!, fd.InstanceLastModifiedOn!, fd.FormLastModifiedOn);
              }
            }
          })
        );
        break;

      case PwaSyncModificationType.Updated:
        // instance loader
        if (folderId) {
          this.syncQueueService.setInstanceState(item.CollectionId, item.InstanceId, OfflineSyncType.Downloading);
        } else {
          this.keepOfflineService.setDownloadingInstance(item.CollectionId, item.InstanceId, item.VersionId, item.WidgetId!, item.ViewListId!);
        }
        //SyncInstanceModified = 4,
        observable = this.keepOfflineService.createFormByInstanceReadResponseObservable(item.CollectionId!, item.InstanceId, item.VersionId!, item.Checksum, PwaSyncType.Sync, item.UpdateTypes!)
          .pipe(
            tap((res) => {
              if (res != null) {
                const fd = res.formData as CollectionForm;
                if (folderId) {
                  this.keepOfflineService.addOfflineFolderInstance(item.InstanceId, item.VersionId!, fd.CollectionFormId!, fd.InstanceLastModifiedOn!, fd.FormLastModifiedOn, folderId, OfflineSyncType.Downloaded);
                } else {
                  this.keepOfflineService.addOfflineInstance(item.CollectionId!, item.InstanceId!, fd.CollectionFormId!, fd.InstanceLastModifiedOn!, fd.FormLastModifiedOn);
                }
              }
            })
            
          );

        break;
      case PwaSyncModificationType.Deleted:
        this.keepOfflineService.deleteInstanceCache(item.CollectionId, item.InstanceId, item.VersionId, folderId, true);
        break;

      case PwaSyncModificationType.Moved:
        this.keepOfflineService.deleteInstanceCache(item.CollectionId, item.InstanceId, item.VersionId, folderId, false);
          break;
    }
    return observable;
  }

}